import type { IReportConfigScreenshotSceneItemEntry } from './interfaces';
import type { ViewsService } from '../../Views.service';
import type { ViewController } from '../../View.controller';
import { ViewPortKind, type IViewPortController, type IViewPortOrientation } from '../../interfaces';
import type { PhaseSwitcher } from './PhaseSwitcher';
import type { IScreenshotBuilder } from './interfaces';
import type { ViewSwitcher } from './ViewSwitcher';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { ImagesService } from '../images/Images.service';
import type * as THREE from 'three';
import type { SliceType } from '../images/interfaces';
import type { OrientationPresetType } from '@/engine/app/modules/reporting/ReportConfigBuilder';
import { ViewPortOrientation } from '@/engine/app/models/ViewPortOrientation';

/**
 * Represents a builder for generating screenshots for a report.
 */
export class ReportScreenshotBuilder implements IScreenshotBuilder {
  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  private readonly _phaseSwitcher: PhaseSwitcher;

  private readonly _viewSwitcher: ViewSwitcher;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _imagesService: ImagesService;

  private _viewPortDescriptor?: string;

  public constructor(
    viewsService: ViewsService,
    viewController: ViewController,
    phaseSwitcher: PhaseSwitcher,
    viewSwitcher: ViewSwitcher,
    planningAlgorithm: PlanningAlgorithm,
    imagesService: ImagesService
  ) {
    this._viewsService = viewsService;
    this._viewController = viewController;
    this._phaseSwitcher = phaseSwitcher;
    this._viewSwitcher = viewSwitcher;
    this._planningAlgorithm = planningAlgorithm;
    this._imagesService = imagesService;
  }

  public init(): void {
    this._viewSwitcher.init();
  }

  private _getViewPortController(): IViewPortController {
    if (!this._viewPortDescriptor) {
      throw new Error('View port descriptor is not set');
    }
    const vpc = this._viewController.getViewPortController(this._viewPortDescriptor);
    if (!vpc) {
      throw new Error('No view port controller for descriptor ' + this._viewPortDescriptor);
    }
    return vpc;
  }

  public reset(): this {
    this._viewsService.currentView?.getObjects().forEach((object) => {
      object.setVisibility(false);
    });
    this._viewsService.currentView?.getCenterLines().forEach((centerLine) => {
      centerLine.setVisibility(false);
    });
    this._viewsService.currentView?.getMeasurements().forEach((measurement) => {
      measurement.setVisibility(false);
    });
    return this;
  }

  public setViewPortDescriptor(viewPortDescriptor: string): this {
    this._viewPortDescriptor = viewPortDescriptor;
    return this;
  }

  public setItems(items: Array<IReportConfigScreenshotSceneItemEntry>): this {
    items.forEach((item: IReportConfigScreenshotSceneItemEntry) => {
      const object = this._viewsService.currentView?.getObjectByDescriptor(item.descriptor);
      if (object) {
        object.setVisibility(true);
        object.setContourVisibility(!!item.contourVisible);
        object.setTransparency(item.transparency);
        object.setHighlighting(!!item.highlighted);
        return;
      }
      const measurement = this._viewsService.currentView?.getMeasurementByDescriptor(item.descriptor);
      if (measurement) {
        measurement.setVisibility(true);
        measurement.setTransparency(item.transparency);
        if (item.selected !== undefined) {
          measurement.setSelection(item.selected);
        }

        return;
      }
      const centerline = this._viewsService.currentView
        ?.getCenterLines()
        .find((centerLine) => centerLine.descriptor === item.descriptor);
      if (centerline) {
        centerline.setVisibility(true);
        return;
      }
    });
    return this;
  }

  public setCamera(orientation: IViewPortOrientation): this {
    const vpc = this._getViewPortController();
    const cameraController = vpc.getCameraController();
    const factor = cameraController.getCameraClone().zoom;
    vpc.setupCamera({ topVector: orientation.topVector, directionVector: orientation.directionVector });
    vpc.fitCameraToObjects();
    cameraController.zoomCamera(factor);

    return this;
  }

  public async setSliceOrientation(sliceType: SliceType, orientationByPreset: OrientationPresetType): Promise<this> {
    const orientation = this.resolvePresetOrientation(orientationByPreset);

    this._imagesService.setSliceOrientation(sliceType, orientation.directionVector, orientation.topVector);

    // TODO: find out why the hell we need to schedule microtask here? (otherwise camera is not fitted)
    await Promise.resolve().then(() => {
      const fitObjects = this._viewController
        .getRegisteredViewPorts(ViewPortKind.ThreeD)
        .at(0)
        ?.getViewPortController()
        .getFitObjects();

      if (fitObjects) {
        const vpc = this._getViewPortController();
        vpc.getCameraController().fitCameraToObjects(fitObjects);
      }
    });

    return this;
  }

  public resolvePresetOrientation(
    presetDescriptor: OrientationPresetType,
    isThreeDScene?: boolean
  ): IViewPortOrientation {
    const frontOrientationProps = this._planningAlgorithm.getValueStrict(presetDescriptor, DT.Orientation).value;
    const orientation: IViewPortOrientation = new ViewPortOrientation(
      frontOrientationProps.top,
      frontOrientationProps.dir
    );

    if (isThreeDScene) {
      orientation.directionVector.negate();
    }

    return orientation;
  }

  public async setViewRoi(locationDescriptor: string, zoomFactor: number): Promise<this> {
    const vpc = this._getViewPortController();
    const location = await this._planningAlgorithm.getValueStrictAsync(locationDescriptor, DT.RegionOfInterest);
    vpc.setViewRoi(location.value, zoomFactor);

    return this;
  }

  public async setViewRoiOffset(locationDescriptor: string): Promise<this> {
    const offset = 50;

    const vpc = this._getViewPortController();
    const location = await this._planningAlgorithm.getValueStrictAsync(locationDescriptor, DT.RegionOfInterest);

    location.value.maxCorner = location.value.maxCorner.addScalar(offset);
    location.value.minCorner = location.value.minCorner.subScalar(offset);

    vpc.setViewRoi(location.value, 1);

    return this;
  }

  public syncImages(point: THREE.Vector3): Promise<this> {
    this._imagesService.syncImages(point);
    return Promise.resolve(this);
  }

  public async switchPhaseByKind(kind: 'endSystole' | 'endDiastole'): Promise<this> {
    await this._phaseSwitcher.switchPhaseByKind(kind);
    return this;
  }

  public switchView(descriptor: string): Promise<this> {
    this._viewSwitcher.switchView(descriptor);
    if (this._imagesService.isReady()) {
      return Promise.resolve(this);
    }
    return new Promise((resolve) => {
      const subscription = this._imagesService.subscribeToReadyStateChange((isReady) => {
        if (isReady) {
          resolve(this);
          subscription?.unsubscribe();
        }
      });
    });
  }

  public setDimensions(dimensions: { width: number; height: number }): this {
    const vpc = this._getViewPortController();
    vpc.setSizeExplicitly(dimensions.width, dimensions.height);

    return this;
  }

  public async getScreenshot(): Promise<string> {
    const vpc = this._getViewPortController();
    return new Promise((resolve) => {
      //TODO: this looks strange
      requestAnimationFrame(() => {
        resolve(vpc.getScreenshot());
      });
    });
  }
}
